import { Link } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Save, Loader2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface Template {
  id: number;
  name: string;
  template_type: string;
  subject_template: string;
  body_template: string;
  active: boolean;
}

interface Props {
  templates: Template[];
}

const TYPE_LABELS: Record<string, string> = {
  candidate_outreach:     "Candidate Outreach",
  firm_outreach:          "Firm Outreach",
  firm_skm:               "Skill Marketing",
  resume_permission:      "Resume Permission",
  interview_coordination: "Interview Coordination",
  submission:             "Submission",
};

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

function TemplateEditor({ template: initial }: { template: Template }) {
  const [open, setOpen]         = useState(false);
  const [template, setTemplate] = useState(initial);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch(`/settings/email-templates/${template.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          email_template: {
            name:             template.name,
            subject_template: template.subject_template,
            body_template:    template.body_template,
            active:           template.active,
          }
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.errors ? JSON.stringify(data.errors) : "Save failed"); return; }
      setTemplate(data as Template);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${template.active ? "bg-green-500" : "bg-gray-300"}`} />
          <span className="text-sm font-medium text-gray-900">{template.name}</span>
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-3 bg-white">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={template.active}
                onChange={(e) => setTemplate((t) => ({ ...t, active: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Active
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input
              value={template.name}
              onChange={(e) => setTemplate((t) => ({ ...t, name: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Subject Template <span className="text-gray-400 font-normal">(use {"{{object.field}}"} for variables)</span>
            </label>
            <input
              value={template.subject_template}
              onChange={(e) => setTemplate((t) => ({ ...t, subject_template: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Body Template</label>
            <textarea
              value={template.body_template}
              onChange={(e) => setTemplate((t) => ({ ...t, body_template: e.target.value }))}
              rows={10}
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saved ? "Saved!" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function EmailTemplates({ templates }: Props) {
  const grouped = Object.entries(TYPE_LABELS).map(([type, label]) => ({
    type,
    label,
    templates: templates.filter((t) => t.template_type === type),
  })).filter((g) => g.templates.length > 0);

  return (
    <AppLayout title="Email Templates">
      <div className="max-w-2xl space-y-5">
        <Link href="/settings" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={14} />
          Settings
        </Link>

        <div>
          <h1 className="text-xl font-semibold text-gray-900">Email Templates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage the base templates used by the email composer. Use{" "}
            <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{"{{object.field}}"}</code>{" "}
            for variable interpolation. AI polish refines the rendered output.
          </p>
        </div>

        {grouped.map(({ type, label, templates: group }) => (
          <div key={type}>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label}</h2>
            <div className="space-y-2">
              {group.map((t) => <TemplateEditor key={t.id} template={t} />)}
            </div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
